import React, { useEffect, useState } from 'react';
import DOMPurify from 'dompurify';
import { toast } from 'sonner';
import { Info } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '../ui/dialog';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../ui/tabs';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import SignatureEditor from '../SignatureEditor';
import { cn } from '../../lib/utils';

/**
 * Was this signature made in the visual builder? Only then can the builder
 * reopen it: it rebuilds its HTML from `signature_data` and emits that on mount,
 * so a signature without it (the auto-created default, anything made in the
 * Sequence Editor, a legacy admin template) would come back as an empty layout
 * and be overwritten on Save. Those open in the HTML-only mode instead (#955,
 * shared by both hosts since #970).
 */
export function madeInBuilder(signatureData) {
    return !!signatureData && Object.keys(signatureData).length > 0;
}

const EDITOR_MODES = [
    { id: 'visual', label: 'Visual' },
    { id: 'html', label: 'HTML' },
    { id: 'text', label: 'Text' },
];

/**
 * The one signature builder dialog (#970). Admin → Signatures and Settings →
 * Templates & Signatures (#955) both render it; only what the host passes in
 * differs (admin: preview-as + template variables + Org-wide; Settings: Private /
 * Shared and the default switch).
 *
 * Layout: the header (title, description, Visual / HTML / Text) and the footer
 * (default switch slot, Cancel, Save) never move; only the body scrolls. The
 * dialog sizes to the window: 95vw x 90vh, at most 1240 x 880.
 *
 * Tabs: General (name + the host's `visibility` node) and the builder's Layout
 * & Style / Photos / Fields. A signature with no builder data (`htmlOnly`) gets
 * General + "Signature (HTML)" instead, edited as HTML, because the builder would
 * rebuild an empty layout and overwrite it on Save.
 *
 * Save: an empty name, or an invalid value in a builder tab, opens that tab,
 * marks it, and refuses; otherwise `onSave()` runs.
 */
export default function SignatureBuilderDialog({
    open,
    onOpenChange,
    title,
    description,
    isAdmin = false,
    mergeFields = false,
    editorKey,
    // builder
    value,
    onChange,
    signatureData,
    onDataChange,
    // HTML-only mode
    htmlOnly = false,
    onHtmlChange,
    resolvePreview = (html) => html,
    // General tab
    name,
    onNameChange,
    nameTestId = 'signature-name-input',
    visibility = null,
    generalExtra = null,
    // footer
    footerStart = null,
    onSave,
    saving = false,
    saveLabel = 'Save signature',
    saveTestId = 'signature-save-btn',
}) {
    const [viewMode, setViewMode] = useState('visual');
    const [tab, setTab] = useState('general');
    const [editorErrors, setEditorErrors] = useState({ style: false, photos: false });
    const [attempted, setAttempted] = useState(false);

    // A fresh signature (new key) opens on General, in Visual, with no errors shown.
    useEffect(() => {
        if (!open) return;
        setViewMode('visual');
        setTab('general');
        setEditorErrors({ style: false, photos: false });
        setAttempted(false);
    }, [open, editorKey, htmlOnly]);

    const nameMissing = !String(name || '').trim();
    const htmlMissing = htmlOnly && !String(value || '').trim();
    const showNameError = attempted && nameMissing;

    const handleSave = () => {
        setAttempted(true);
        const firstBad = nameMissing ? 'general'
            : htmlMissing ? 'html'
                : (!htmlOnly && editorErrors.style) ? 'style'
                    : (!htmlOnly && editorErrors.photos) ? 'photos'
                        : null;
        if (firstBad) {
            setViewMode('visual');
            setTab(firstBad);
            toast.error(nameMissing ? 'Give the signature a name' : 'Fix the highlighted values first');
            return;
        }
        onSave?.();
    };

    const generalContent = (
        <div className="space-y-6">
            <div className="space-y-2">
                <Label htmlFor="signature-builder-name">Signature name</Label>
                <Input
                    id="signature-builder-name"
                    data-testid={nameTestId}
                    value={name || ''}
                    onChange={e => onNameChange?.(e.target.value)}
                    placeholder="e.g. Sales team · standard"
                    aria-invalid={showNameError || undefined}
                    aria-describedby={showNameError ? 'signature-builder-name-error' : undefined}
                    className={cn('max-w-xl', showNameError && 'border-destructive focus-visible:ring-destructive')}
                />
                {showNameError && (
                    <p id="signature-builder-name-error" role="alert" className="text-xs text-destructive">Give the signature a name.</p>
                )}
            </div>
            {visibility}
            {generalExtra}
        </div>
    );

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent
                className="flex h-[90vh] max-h-[880px] w-[95vw] max-w-[1240px] flex-col gap-0 overflow-hidden p-0"
                data-testid="signature-builder-dialog"
            >
                {/* Header: fixed */}
                <div className="flex shrink-0 flex-wrap items-start gap-4 border-b px-6 pb-4 pt-5 pr-14">
                    <div className="min-w-0 flex-1 space-y-1.5">
                        <DialogTitle>{title}</DialogTitle>
                        {description && <DialogDescription>{description}</DialogDescription>}
                    </div>
                    {!htmlOnly && (
                        <Tabs value={viewMode} onValueChange={setViewMode}>
                            <TabsList aria-label="Editor mode">
                                {EDITOR_MODES.map(m => (
                                    <TabsTrigger key={m.id} value={m.id} data-testid={`signature-mode-${m.id}`}>{m.label}</TabsTrigger>
                                ))}
                            </TabsList>
                        </Tabs>
                    )}
                </div>

                {/* Body: the only part that scrolls */}
                <div className="min-h-0 flex-1">
                    {htmlOnly ? (
                        <HtmlOnlyBody
                            tab={tab}
                            onTabChange={setTab}
                            generalContent={generalContent}
                            generalHasError={showNameError}
                            html={value}
                            onHtmlChange={onHtmlChange}
                            htmlHasError={attempted && htmlMissing}
                            resolvePreview={resolvePreview}
                        />
                    ) : (
                        <SignatureEditor
                            key={editorKey}
                            value={value}
                            onChange={onChange}
                            signatureData={signatureData}
                            onDataChange={onDataChange}
                            isAdmin={isAdmin}
                            mergeFields={mergeFields}
                            viewMode={viewMode}
                            tab={tab}
                            onTabChange={setTab}
                            generalTab={{ content: generalContent, hasError: showNameError }}
                            onValidityChange={setEditorErrors}
                        />
                    )}
                </div>

                {/* Footer: fixed */}
                <div className="flex shrink-0 flex-wrap items-center gap-3 border-t px-6 py-4">
                    <div className="mr-auto">{footerStart}</div>
                    <Button type="button" variant="outline" onClick={() => onOpenChange?.(false)}>Cancel</Button>
                    <Button type="button" onClick={handleSave} disabled={saving} data-testid={saveTestId}>
                        {saving ? 'Saving…' : saveLabel}
                    </Button>
                </div>
            </DialogContent>
        </Dialog>
    );
}

/**
 * HTML-only body (#970 D10, from #955): General + "Signature (HTML)". The HTML is
 * editable here because there is no builder data to rebuild it from.
 */
function HtmlOnlyBody({ tab, onTabChange, generalContent, generalHasError, html, onHtmlChange, htmlHasError, resolvePreview }) {
    return (
        <Tabs value={tab} onValueChange={onTabChange}
            className="grid h-full min-h-0 grid-cols-1 md:grid-cols-[minmax(0,1fr)_minmax(340px,440px)]">
            <div className="flex min-h-0 flex-col">
                <div className="shrink-0 px-6 pt-5">
                    <TabsList aria-label="Signature sections">
                        <TabsTrigger value="general" data-testid="signature-tab-general">
                            General
                            {generalHasError && <span className="ml-1.5 h-1.5 w-1.5 rounded-full bg-destructive" aria-label="has errors" />}
                        </TabsTrigger>
                        <TabsTrigger value="html" data-testid="signature-tab-html">
                            Signature (HTML)
                            {htmlHasError && <span className="ml-1.5 h-1.5 w-1.5 rounded-full bg-destructive" aria-label="has errors" />}
                        </TabsTrigger>
                    </TabsList>
                </div>
                <div className="min-h-0 flex-1 overflow-y-auto px-6 pb-6 pt-4">
                    <TabsContent value="general" forceMount className="mt-0 data-[state=inactive]:hidden">
                        <Card className="shadow-sm"><CardContent className="p-6">{generalContent}</CardContent></Card>
                    </TabsContent>
                    <TabsContent value="html" forceMount className="mt-0 data-[state=inactive]:hidden" data-testid="signature-html-editor">
                        <Card className="shadow-sm">
                            <CardContent className="space-y-3 p-6">
                                <div role="note" className="flex gap-2 rounded-md border bg-muted/40 p-3 text-sm">
                                    <Info className="mt-0.5 h-4 w-4 shrink-0" />
                                    <span>
                                        <span className="font-medium">Made outside the builder.</span> This signature has no builder
                                        settings, so it is edited as HTML. Merge fields such as {'{{SenderName}}'} fill in from the sender.
                                    </span>
                                </div>
                                <Label htmlFor="signature-html">Signature (HTML)</Label>
                                <textarea
                                    id="signature-html"
                                    data-testid="signature-html-input"
                                    value={html || ''}
                                    onChange={e => onHtmlChange?.(e.target.value)}
                                    spellCheck={false}
                                    aria-invalid={htmlHasError || undefined}
                                    className={cn(
                                        'min-h-[360px] w-full resize-y rounded-md border border-input bg-transparent px-3 py-2 font-mono text-xs shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring',
                                        htmlHasError && 'border-destructive'
                                    )}
                                />
                                {htmlHasError && <p role="alert" className="text-xs text-destructive">The signature is empty.</p>}
                            </CardContent>
                        </Card>
                    </TabsContent>
                </div>
            </div>
            <aside aria-label="Live preview" className="flex min-h-0 flex-col gap-3 overflow-y-auto border-t bg-muted/40 p-6 md:border-l md:border-t-0">
                <h3 className="text-sm font-semibold">Live preview</h3>
                <div className="rounded-lg border bg-white p-5 shadow-sm">
                    <div
                        data-testid="signature-html-preview"
                        dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(resolvePreview(html || '') || '') }}
                    />
                </div>
                <p className="text-xs text-muted-foreground">Your HTML after cleaning: scripts and event handlers are removed.</p>
            </aside>
        </Tabs>
    );
}
